import toast from "react-hot-toast";

import { router } from "@/main";

import { nextCargoApi } from "../../baseApi";
import { truckIsTruckArgsSchema } from "./truck.schema";
import { GetTruck, IsTruck, SaveDeleteTruck, SaveEditTruck, SaveNewTruck, SaveRebindTruck, SaveUnbindTruck, SearchTruck } from "./truck.types";

export const truckApi = nextCargoApi.injectEndpoints({
  endpoints: (build) => ({
    getIsTruck: build.query<IsTruck["result"], IsTruck["args"]>({
      query: ({ plate }) => `/truck/isTruck/${plate}`,
      argSchema: truckIsTruckArgsSchema
    }),
    getTruck: build.query<GetTruck["result"], GetTruck["args"]>({
      query: ({ id }) => `/truck/${id}`,
      keepUnusedDataFor: 0,
      // transformResponse: (response) => {
      //   const normalizedRes = structuredClone(response);
      //   normalizedRes.driver.name = normalizedRes.driver.name.replace(/\s+/g, " ").trim();

      //   return normalizedRes;
      // },
      onQueryStarted: async (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando caminhão...",
          success: "Caminhão carregado com sucesso!"
          // error: "Erro ao carregar caminhão."
        });
      }
    }),
    saveEditTruck: build.mutation<SaveEditTruck["result"], SaveEditTruck["args"]>({
      query: (body) => ({
        url: `/truck/app/edit`,
        method: "POST",
        body
      })
    }),
    saveNewTruck: build.mutation<SaveNewTruck["result"], SaveNewTruck["args"]>({
      query: (args) => {
        const { id = null, vehicleModel, company, driver, shippingCompany, renavam, isEnabled, ...rest } = args;

        const toNumber = (val?: string | number | null) => {
          if (val === null || val === undefined) return null;
          return typeof val === "string" ? Number(val) || null : val;
        };

        const body = {
          ...rest,
          id: id ? toNumber(id) : null,
          vehicleModel: vehicleModel?.id
            ? {
                id: toNumber(vehicleModel.id),
                truckBrand: vehicleModel.truckBrand?.id ? { id: toNumber(vehicleModel.truckBrand.id) } : undefined
              }
            : null,
          renavam: renavam?.replace(/\D/g, ""),
          company: company?.id ? { id: toNumber(company.id) } : null,
          driver: driver?.id ? { id: toNumber(driver.id) } : null,
          shippingCompany: shippingCompany?.id ? { id: toNumber(shippingCompany.id) } : null,
          isEnabled,
          status: isEnabled
        };

        return {
          url: "/truck/save",
          method: "POST",
          body
        };
      },
      onQueryStarted: async ({ id, driver, ...truck }, { queryFulfilled, dispatch }) => {
        const truckId = id;
        const driverId = driver.id;

        const isEdit = Boolean(truckId);
        toast.promise(queryFulfilled, {
          loading: isEdit ? "Atualizando caminhão..." : "Criando caminhão...",
          success: isEdit ? "Caminhão atualizado com sucesso!" : "Caminhão criado com sucesso!"
        });

        try {
          await queryFulfilled;
        } catch (error: unknown) {
          const err = error as {
            error: {
              status: number;
              data: {
                message: string;
                truckId: number;
                field: string;
                existingDriverId: number;
              };
            };
            isUnhandledError: boolean;
          };
          const errStatus = err.error.status;
          const errData = err.error.data;

          if (errStatus === 422) {
            const { error } = await dispatch(
              truckApi.endpoints.saveRebindTruck.initiate({
                driver: { id: driverId },
                //@ts-expect-error - Erro chato de tipagem que não interfere em nada na lógica
                truck: { id: errData.truckId, ...truck }
              })
            );
            if (!error) {
              router.navigate({ to: "/app" });
              return;
            }
            return;
          }

          if (errStatus === 409) {
            toast.error("Você está tentando criar um caminhão no qual você já possui no seu cadastro.");
            return;
          }

          if (isEdit) {
            toast.error("Erro ao atualizar caminhão.");
            return;
          }

          toast.error("Erro ao criar caminhão.", { id: "create-truck-error" });
        }
      }
    }),
    saveDeleteTruck: build.mutation<SaveDeleteTruck["result"], SaveDeleteTruck["args"]>({
      query: ({ id }) => ({
        url: `/truck/delete/${id}`,
        method: "DELETE"
      }),
      onQueryStarted: async (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Excluindo caminhão...",
          success: "Caminhão excluído com sucesso!",
          error: "Erro ao excluir caminhão."
        });
      }
    }),
    saveRebindTruck: build.mutation<SaveRebindTruck["result"], SaveRebindTruck["args"]>({
      query: (args) => {
        const renavamFormatted = args.truck?.renavam?.replace(/\D/g, "");

        return {
          url: "/truck/rebind",
          method: "PATCH",
          body: {
            ...args,
            truck: args.truck
              ? {
                  ...args.truck,
                  renavam: renavamFormatted
                }
              : undefined
          }
        };
      },
      onQueryStarted: async (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Reatribuindo caminhão...",
          success: "Caminhão reatribuído com sucesso!"
        });

        try {
          await queryFulfilled;
        } catch (error: unknown) {
          const err = error as {
            error: {
              status: number;
              data: {
                message: string;
                driver: {
                  id: number;
                  name: string;
                  isEnabled: boolean;
                  lastBlockDate: string | null;
                  deletedAt: string | null;
                  createdAt: string;
                  updatedAt: string | null;
                  company: {
                    id: number;
                    name: string;
                    cnpj: string;
                    company: string;
                    uf: string;
                    street: string;
                    number: string;
                    complement: string;
                    city: string;
                    district: string;
                    zipCode: string;
                    status: boolean;
                    website: string;
                    logo: string;
                  };
                };
              };
            };
            isUnhandledError: boolean;
          };
          const errData = err.error?.data;

          if (errData?.message) {
            toast.error(`Erro ao reatribuir caminhão: ${errData.message}`, { position: "top-right" });
            return;
          }

          toast.error("Um erro desconhecido ocorreu ao reatribuir caminhão. Tente novamente mais tarde.", { position: "top-right" });
        }
      }
    }),
    saveUnbindTruck: build.mutation<SaveUnbindTruck["result"], SaveUnbindTruck["args"]>({
      query: (body) => ({ url: "/truck/unbind", method: "PATCH", body })
    }),
    searchTruck: build.query<SearchTruck["result"], SearchTruck["args"]>({
      query: ({ licensePlate }) => {
        const formattedPlate = licensePlate.trim().replace(/.*- /, "").replace(/\s+/g, "");

        return {
          url: `/truck/like/${formattedPlate}`
        };
      }
    })
  })
});

truckApi.enhanceEndpoints({
  addTagTypes: ["IsTruck"],
  endpoints: {
    getIsTruck: {
      providesTags: ["IsTruck"]
    },
    getTruck: {
      providesTags: (result, error, { id }) => [{ type: "Truck", id }]
    },
    saveEditTruck: {
      invalidatesTags: (result, error, { id }) => (id ? [{ type: "Truck", id }] : ["Truck"])
    },
    saveNewTruck: {
      invalidatesTags: (result, error, { id }) => (id ? [{ type: "Truck", id }] : ["Driver"])
    },
    saveDeleteTruck: {
      invalidatesTags: (result, error, { id }) => [{ type: "Truck", id }, "Driver"]
    },
    saveRebindTruck: {
      invalidatesTags: () => ["Truck", "Driver"]
    },
    saveUnbindTruck: {
      invalidatesTags: () => ["Truck", "Driver"]
    },
    searchTruck: {
      providesTags: (result) =>
        result ? [...result.map(({ id }) => ({ type: "TruckSearch" as const, id })), { type: "TruckSearch", id: "LIST" }] : [{ type: "TruckSearch", id: "LIST" }]
    }
  }
});
